import { createValidatorForFields, formFieldsFromSettings, getFieldType } from '@jetshop/core/components/Auth/formFieldsFromSettings';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import logError from '@jetshop/core/helpers/logError';
import t from '@jetshop/intl';
import { Form, Formik } from 'formik';
import React, { useContext, useMemo } from 'react';
import { getAddressInputFieldName, getCustomerUpdateInput, useCustomerQuery, useCustomerUpdateMutation } from './useCustomer';
export function getBillingAddressFieldsToRender(fields) {
    return fields
        .map((field) => {
        const name = `billingAddress.${getAddressInputFieldName(field.name)}`;
        if (name === `billingAddress.countryCode`) {
            // omit changing `countryCode` for now
            return null;
        }
        const inputProps = {
            name,
            label: field.label,
            id: field.id,
            required: field.required,
            type: getFieldType(field.name)
        };
        return { inputProps };
    })
        .filter(Boolean);
}
export function getDeliveryAddressFieldsToRender(fields, index) {
    return fields
        .map((field) => {
        const name = `deliveryAddresses[${index}].${getAddressInputFieldName(field.name)}`;
        if (name === `deliveryAddresses[${index}].countryCode`) {
            // omit changing `countryCode` for now
            return null;
        }
        const inputProps = {
            name,
            label: field.label,
            id: field.id,
            required: field.required,
            type: getFieldType(field.name)
        };
        return { inputProps };
    })
        .filter(Boolean);
}
export const CustomerUpdateFormProvider = ({ render, onSubmit, onSubmitSuccess, customerProfileQuery, deliveryAddressId }) => {
    const { customer, countrySettings, setCountryCode, loading } = useCustomerQuery({
        query: customerProfileQuery
    });
    const { selectedChannel } = useContext(ChannelContext);
    const isBusinessCustomer = customer.__typename === 'BusinessCustomer';
    const { billingAddressFields, deliveryAddressFields, pidField } = useMemo(() => formFieldsFromSettings(countrySettings, isBusinessCustomer), [countrySettings, isBusinessCustomer]);
    const billingAddressFieldsToRender = useMemo(() => getBillingAddressFieldsToRender(billingAddressFields), [billingAddressFields]);
    const deliveryAddressIndex = customer &&
        customer.deliveryAddresses.findIndex((a) => a.id === deliveryAddressId);
    const deliveryAddressFieldsToRender = useMemo(() => deliveryAddressIndex > -1
        ? getDeliveryAddressFieldsToRender(deliveryAddressFields, deliveryAddressIndex)
        : [], [deliveryAddressFields, deliveryAddressIndex]);
    const initialValues = useMemo(() => getCustomerUpdateInput({
        customer,
        billingAddressFields,
        culture: selectedChannel.language.culture,
        countryCode: selectedChannel.country.code
    }), [
        customer,
        billingAddressFields,
        selectedChannel.language.culture,
        selectedChannel.country.code
    ]);
    const billingAddressValidator = useMemo(() => addressValidator(billingAddressFields), [billingAddressFields]);
    const deliveryAddressValidator = useMemo(() => addressValidator(deliveryAddressFields), [deliveryAddressFields]);
    function addressValidator(fields) {
        return createValidatorForFields(fields, {
            getFieldName(name) {
                const fieldName = getAddressInputFieldName(name);
                return fieldName;
            }
        });
    }
    const customerUpdateMutation = useCustomerUpdateMutation({
        customerQuery: customerProfileQuery
    });
    return loading ? null : (React.createElement(Formik, { initialValues: initialValues, validate: (values) => {
            const errors = {};
            const pidFieldName = pidField.name;
            if (pidFieldName in values &&
                pidField.required &&
                (values[pidFieldName] || '').trim().length === 0) {
                errors[pidFieldName] = t('This field is required');
            }
            const billingAddress = billingAddressValidator(values.billingAddress);
            if (Object.keys(billingAddress).length > 0) {
                errors.billingAddress = billingAddress;
            }
            if (deliveryAddressId !== undefined) {
                const deliveryAddress = deliveryAddressValidator(values.deliveryAddresses[deliveryAddressIndex]);
                if (Object.keys(deliveryAddress).length > 0) {
                    errors.deliveryAddresses = [];
                    errors.deliveryAddresses[deliveryAddressIndex] = deliveryAddress;
                }
            }
            return errors;
        }, onSubmit: (values, actions) => {
            actions.setSubmitting(true);
            onSubmit && onSubmit();
            customerUpdateMutation(values)
                .then(() => {
                actions.setSubmitting(false);
                actions.resetForm();
                onSubmitSuccess && onSubmitSuccess();
            })
                .catch((err) => {
                logError(true, err);
                actions.setSubmitting(false);
                actions.setStatus({
                    globalError: t('Something went wrong saving information. Try again?')
                });
            });
        }, render: (props) => (React.createElement(Form, null, render(Object.assign(Object.assign({}, props), { billingAddressFields: billingAddressFieldsToRender, deliveryAddressFields: deliveryAddressFieldsToRender, pidField,
            setCountryCode, countries: selectedChannel.countries })))) }));
};
//# sourceMappingURL=CustomerUpdateForm.js.map